import InsightsIcon from '@mui/icons-material/Insights';
import { Box, Button, Chip, Divider, Fade, Grow, Typography } from '@mui/material';
import React, { useCallback, useMemo, useState } from 'react';
import { useDataProvider } from 'react-admin';
import { getChainId } from '../../../../lib';
import { RuleGroup } from './types';

interface PerGroupResult {
  groupId: number;
  matchingCustomers: number;
  rules: Array<{
    ruleIndex: number;
    matchingCustomers: number;
    simulatable: boolean;
  }>;
}

interface SimulationData {
  totalCustomers: number;
  matchingCustomers: number;
  perGroupResults: PerGroupResult[];
  nonSimulatableTypes: string[];
  period: { days: number };
}

interface GiftRulesSimulatorPanelProps {
  ruleGroups: RuleGroup[];
}

const SIMULATABLE_TYPES = new Set(['fillin', 'accumulated_fillin']);

const NON_SIMULATABLE_LABELS: Record<string, string> = {
  clube_certo: 'Clube Certo',
  pix_payment: 'Pagamento Pix',
  points_exchange: 'Troca por Pontos',
};

const shimmerKeyframes = {
  '@keyframes shimmer': {
    '0%': { backgroundPosition: '200% 0' },
    '100%': { backgroundPosition: '-200% 0' },
  },
};

const GiftRulesSimulatorPanel: React.FC<GiftRulesSimulatorPanelProps> = ({ ruleGroups }) => {
  const dataProvider = useDataProvider();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<SimulationData | null>(null);
  const [days, setDays] = useState(30);
  const [hasFetched, setHasFetched] = useState(false);

  // Verificar se há regras simuláveis
  const hasSimulatableRules = useMemo(() => {
    return ruleGroups.some(group =>
      group.rules.some(rule => SIMULATABLE_TYPES.has(rule.type))
    );
  }, [ruleGroups]);

  const totalRules = useMemo(() => {
    return ruleGroups.reduce((sum, group) => sum + group.rules.length, 0);
  }, [ruleGroups]);

  const handleFetch = useCallback(async () => {
    setLoading(true);
    try {
      const chainId = getChainId();

      // Serializar todas as regras com seus groupIds (índice do grupo)
      const allRules = ruleGroups.flatMap((group, groupIndex) =>
        group.rules.map(rule => ({
          type: rule.type,
          fuelIds: rule.fuelIds || [],
          valueType: rule.valueType || 'volume',
          value: rule.value || 0,
          quantity: rule.quantity || 1,
          stationIds: rule.stationIds || [],
          groupId: groupIndex,
        }))
      );

      const response = await dataProvider.getList('gifts/simulate-rules', {
        filter: { chainId, days, rules: JSON.stringify(allRules) },
        pagination: { page: 1, perPage: 1 },
        sort: { field: 'id', order: 'ASC' },
      });

      const result = response.data?.[0] as any;
      if (result) {
        setData(result);
        setHasFetched(true);
      }
    } catch (e) {
      console.error('Combined rules simulation error:', e);
    } finally {
      setLoading(false);
    }
  }, [dataProvider, ruleGroups, days]);

  const showResults = data && hasFetched && !loading;

  if (!hasSimulatableRules || totalRules === 0) return null;

  return (
    <Box
      sx={{
        border: '1px solid rgba(102, 126, 234, 0.15)',
        borderRadius: '12px',
        background: 'rgba(102, 126, 234, 0.03)',
        p: 2,
        mb: 2,
        ...shimmerKeyframes,
      }}
    >
      {/* Header */}
      <Box display="flex" alignItems="center" flexWrap="wrap" gap={1}>
        <InsightsIcon sx={{ color: '#667eea', fontSize: '1.1rem' }} />
        <Typography sx={{ fontSize: '0.83rem', color: '#667eea', fontWeight: 600, mr: 0.5 }}>
          Alcance Estimado
        </Typography>

        <Chip
          label={`${ruleGroups.length} grupo${ruleGroups.length !== 1 ? 's' : ''}`}
          size="small"
          sx={{ height: 20, fontSize: '0.7rem', bgcolor: 'rgba(102,126,234,0.08)', color: '#667eea', fontWeight: 500 }}
        />
        <Chip
          label={`${totalRules} regra${totalRules !== 1 ? 's' : ''}`}
          size="small"
          sx={{ height: 20, fontSize: '0.7rem', bgcolor: 'rgba(102,126,234,0.08)', color: '#667eea', fontWeight: 500 }}
        />

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5, borderColor: 'rgba(102,126,234,0.2)' }} />

        <Box display="flex" alignItems="center" gap={0.5}>
          <Typography sx={{ fontSize: '0.76rem', color: 'text.secondary' }}>últimos</Typography>
          <input
            type="number"
            min={7}
            max={180}
            value={days}
            onChange={e => setDays(Math.min(180, Math.max(7, Number(e.target.value) || 30)))}
            style={{
              width: 36,
              textAlign: 'center',
              border: 'none',
              borderBottom: '1px solid rgba(102,126,234,0.4)',
              background: 'transparent',
              color: '#667eea',
              fontWeight: 700,
              fontSize: '0.76rem',
              outline: 'none',
              padding: '0 2px',
            }}
          />
          <Typography sx={{ fontSize: '0.76rem', color: 'text.secondary' }}>dias</Typography>
        </Box>

        <Button
          variant="outlined"
          size="small"
          startIcon={<InsightsIcon sx={{ fontSize: '0.9rem !important' }} />}
          onClick={handleFetch}
          disabled={loading}
          sx={{
            ml: 'auto',
            borderColor: '#667eea',
            color: '#667eea',
            fontSize: '0.76rem',
            textTransform: 'none',
            py: 0.3,
            px: 1.5,
            '&:hover': { background: 'rgba(102,126,234,0.08)', borderColor: '#5a6fd6' },
          }}
        >
          {showResults ? 'Atualizar' : 'Simular alcance combinado'}
        </Button>
      </Box>

      {/* Loading shimmer */}
      {loading && (
        <Box sx={{ mt: 1.5 }}>
          <Typography sx={{ fontSize: '0.72rem', color: 'rgba(102,126,234,0.6)', mb: 1, fontStyle: 'italic' }}>
            Analisando o alcance combinado de todas as regras nos últimos {days} dias...
          </Typography>
          {[1, 2, 3].map(i => (
            <Box
              key={i}
              sx={{
                height: 32,
                borderRadius: '8px',
                mb: 0.8,
                background: 'linear-gradient(90deg, rgba(102,126,234,0.06) 25%, rgba(118,75,162,0.12) 50%, rgba(102,126,234,0.06) 75%)',
                backgroundSize: '200% 100%',
                animation: 'shimmer 1.5s infinite',
                animationDelay: `${i * 0.2}s`,
              }}
            />
          ))}
        </Box>
      )}

      {/* Results */}
      {showResults && (
        <Fade in timeout={400}>
          <Box sx={{ mt: 1.5 }}>
            {/* Nota sobre regras não-simuláveis */}
            {data.nonSimulatableTypes.length > 0 && (
              <Typography sx={{ fontSize: '0.68rem', color: 'text.disabled', mb: 1, fontStyle: 'italic' }}>
                Regras do tipo {data.nonSimulatableTypes.map(t => NON_SIMULATABLE_LABELS[t] || t).join(', ')} não podem ser simuladas com base em abastecimentos
              </Typography>
            )}

            {data.matchingCustomers === -1 ? (
              <Box sx={{ py: 1.5, px: 1.5, borderRadius: '8px', bgcolor: 'rgba(0,0,0,0.03)', border: '1px solid rgba(0,0,0,0.06)' }}>
                <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>
                  Nenhuma regra simulável encontrada. Apenas regras de abastecimento podem ser simuladas.
                </Typography>
              </Box>
            ) : data.totalCustomers === 0 ? (
              <Box sx={{ py: 1.5, px: 1.5, borderRadius: '8px', bgcolor: 'rgba(0,0,0,0.03)', border: '1px solid rgba(0,0,0,0.06)' }}>
                <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>
                  Nenhum cliente encontrado para os postos e período selecionados.
                </Typography>
              </Box>
            ) : (
              <>
                {/* Main metric */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    px: 1.5,
                    py: 1,
                    borderRadius: '8px',
                    bgcolor: data.matchingCustomers === 0 ? 'rgba(198,40,40,0.06)' : data.matchingCustomers === data.totalCustomers ? 'rgba(46,125,50,0.06)' : 'rgba(102,126,234,0.06)',
                    border: `1px solid ${data.matchingCustomers === 0 ? 'rgba(198,40,40,0.15)' : data.matchingCustomers === data.totalCustomers ? 'rgba(46,125,50,0.15)' : 'rgba(102,126,234,0.12)'}`,
                  }}
                >
                  <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>
                    <Typography component="span" sx={{ fontWeight: 800, fontSize: '0.88rem', color: data.matchingCustomers === 0 ? '#c62828' : data.matchingCustomers === data.totalCustomers ? '#2e7d32' : '#667eea' }}>
                      {data.matchingCustomers} de {data.totalCustomers}
                    </Typography>
                    {' '}cliente{data.totalCustomers !== 1 ? 's' : ''} atingiriam <b>todas</b> as condições
                  </Typography>
                </Box>

                {/* Progress bar */}
                <Box sx={{ mt: 1.2 }}>
                  <Box sx={{ position: 'relative', height: 10, borderRadius: 99, overflow: 'hidden', bgcolor: 'rgba(0,0,0,0.06)' }}>
                    <Box
                      sx={{
                        position: 'absolute',
                        left: 0,
                        top: 0,
                        bottom: 0,
                        width: `${(data.matchingCustomers / data.totalCustomers) * 100}%`,
                        borderRadius: 99,
                        background: data.matchingCustomers === data.totalCustomers
                          ? 'linear-gradient(90deg, #66bb6a, #43a047)'
                          : data.matchingCustomers === 0
                            ? 'linear-gradient(90deg, #ef5350, #e53935)'
                            : 'linear-gradient(90deg, #667eea, #764ba2)',
                        transition: 'width 0.4s ease',
                        boxShadow: data.matchingCustomers > 0 ? '0 0 8px rgba(102,126,234,0.3)' : 'none',
                      }}
                    />
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.3 }}>
                    <Typography sx={{ fontSize: '0.6rem', color: 'text.disabled' }}>
                      {Math.round((data.matchingCustomers / data.totalCustomers) * 100)}% atingiriam
                    </Typography>
                    <Typography sx={{ fontSize: '0.6rem', color: 'text.disabled' }}>
                      {data.matchingCustomers}/{data.totalCustomers}
                    </Typography>
                  </Box>
                </Box>

                {/* Per-group breakdown */}
                {data.perGroupResults.length > 1 && (
                  <Box display="flex" gap={0.8} mt={1.5} flexWrap="wrap">
                    {data.perGroupResults.map((group, idx) => {
                      const hasSimulatable = group.rules.some(r => r.simulatable);
                      if (!hasSimulatable) return null;
                      const pct = data.totalCustomers > 0 ? Math.round((group.matchingCustomers / data.totalCustomers) * 100) : 0;
                      return (
                        <Grow in timeout={400 + idx * 100} key={group.groupId}>
                          <Chip
                            label={`Grupo ${group.groupId + 1}: ${group.matchingCustomers} (${pct}%)`}
                            size="small"
                            sx={{
                              height: 22,
                              fontSize: '0.68rem',
                              bgcolor: 'rgba(102,126,234,0.08)',
                              color: '#667eea',
                              fontWeight: 500,
                              border: '1px solid rgba(102,126,234,0.15)',
                            }}
                          />
                        </Grow>
                      );
                    })}
                  </Box>
                )}
              </>
            )}
          </Box>
        </Fade>
      )}
    </Box>
  );
};

export default GiftRulesSimulatorPanel;
